import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ACHFormValues } from 'adminSignUp/common/ACHPayment'
import { Button, Modal, Skeleton } from 'antd'

import { RenewalUpdatePaymentInformationDTO } from 'core/application/adminDashboard/benefitRenewal/renewalPaymentInformation/renewalPaymentInformation'
import getSubscriptionDetails, {
  ACHDetails,
  CreditCardDetails,
  PaymentMethod,
  Subscription,
} from 'core/application/adminDashboard/companySettings/subscription/subscriptionDetails'

import { getAchToken, getCreditCardToken } from 'common/recultyUtils'

import { useCommandRequest, useQueryRequest } from '../../../../common/hooks'
import EditPaymentInformation from './EditPaymentInformation'
import ViewACHInfo from './ViewACHInfo'
import ViewCreditCardInfo from './ViewCreditCardInfo'

const getDefaultAchFormValues = (isACH: boolean, paymentDetails: ACHDetails): ACHFormValues => {
  return {
    agreeTerms: false,
    bankAccountType: isACH ? paymentDetails.accountType : '',
    bankRoutingNumber: isACH ? paymentDetails.accountNumber : '',
    accountNumberConfirmation: '',
    accountNumber: '',
    billingEmail: '',
    nameOnAccount: isACH ? paymentDetails.nameOnAccount : '',
  }
}

type ConfirmPaymentInformationModalProps = {
  updateRenewalPaymentInformationHandler: (value: RenewalUpdatePaymentInformationDTO) => Promise<any>
  onRenewalPaymentInfoUpdated?: () => void
  onCancel?: () => void
}

const ConfirmPaymentInformationModal: React.FC<ConfirmPaymentInformationModalProps> = (props) => {
  const { t: translate } = useTranslation()
  const [subscription] = useQueryRequest<Subscription>(null, () => getSubscriptionDetails())
  const [sendCommand] = useCommandRequest()
  const [isSubmitHandlerLoading, setIsSubmitHandlerLoading] = useState(false)

  const [viewMode, setViewMode] = useState<boolean>(true)
  const [localPaymentMethod, setLocalPaymentMethod] = useState<string | null>(subscription?.paymentMethod.toString())
  const [achFormValues, setAchFormValues] = useState<ACHFormValues>()
  const [recurlyErrors, setRecurlyErrors] = useState<Map<string, string>>(new Map())

  const isCreditCard = () => subscription?.paymentMethod == PaymentMethod.CreditCard
  const isLocalCreditCard = () => localPaymentMethod == PaymentMethod.CreditCard.toString()

  useEffect(() => {
    if (subscription) {
      setAchFormValues(getDefaultAchFormValues(!isCreditCard(), subscription.paymentDetails as ACHDetails))
    }
  }, [subscription])

  const useSame = async () => {
    setIsSubmitHandlerLoading(true)
    const result = await sendCommand(() =>
      props.updateRenewalPaymentInformationHandler({
        useSame: true,
      }),
    )

    if (result && props.onRenewalPaymentInfoUpdated) {
      props.onRenewalPaymentInfoUpdated()
    }
    setIsSubmitHandlerLoading(false)
  }

  const handleSubmit = async (value?: ACHFormValues) => {
    try {
      setIsSubmitHandlerLoading(true)
      const recurlyToken = value !== undefined ? await getAchToken(value) : await getCreditCardToken()
      const result = await sendCommand(() =>
        props.updateRenewalPaymentInformationHandler({
          useSame: false,
          recurlyToken,
          paymentMethod: value !== undefined ? PaymentMethod.ACH : PaymentMethod.CreditCard,
        }),
      )

      if (result && props.onRenewalPaymentInfoUpdated) {
        props.onRenewalPaymentInfoUpdated()
      }
      setIsSubmitHandlerLoading(false)
    } catch (errors) {
      setRecurlyErrors(errors)
      setIsSubmitHandlerLoading(false)
    }
  }

  const footer = (
    <div>
      <Button type="default" onClick={() => setViewMode(false)}>
        {translate('admin-dashboard.home.confirm-payment-method.make-changes')}
      </Button>
      <Button type="primary" onClick={useSame} disabled={isSubmitHandlerLoading} loading={isSubmitHandlerLoading}>
        {translate('admin-dashboard.home.confirm-payment-method.use-same')}
      </Button>
    </div>
  )

  const editFooter = (
    <div>
      <Button type="default" onClick={() => setViewMode(true)}>
        {translate('admin-dashboard.home.confirm-payment-method.go-back')}
      </Button>
      {isLocalCreditCard() || (
        <Button
          disabled={isSubmitHandlerLoading}
          loading={isSubmitHandlerLoading}
          form="paymentInformationForm"
          type="primary"
          htmlType="submit"
        >
          {translate('general.save')}
        </Button>
      )}
      {isLocalCreditCard() && (
        <Button
          disabled={isSubmitHandlerLoading}
          loading={isSubmitHandlerLoading}
          type="primary"
          onClick={() => handleSubmit()}
        >
          {translate('general.save')}
        </Button>
      )}
    </div>
  )

  return (
    <Modal
      width="800px"
      open={true}
      onCancel={props.onCancel}
      title={translate('admin-dashboard.home.confirm-payment-method.title')}
      footer={viewMode ? footer : editFooter}
    >
      <Skeleton loading={!subscription}>
        {subscription && (
          <>
            {viewMode ? (
              isCreditCard() ? (
                <ViewCreditCardInfo paymentDetails={subscription.paymentDetails as CreditCardDetails} />
              ) : (
                <ViewACHInfo paymentDetails={subscription.paymentDetails as ACHDetails} />
              )
            ) : (
              <EditPaymentInformation
                achFormValues={achFormValues}
                recurlyErrors={recurlyErrors}
                paymentMethod={localPaymentMethod}
                paymentDetails={subscription.paymentDetails}
                submitACH={handleSubmit}
                setPaymentMethod={setLocalPaymentMethod}
              />
            )}
          </>
        )}
      </Skeleton>
    </Modal>
  )
}

export default ConfirmPaymentInformationModal
